import * as XLSX from "xlsx";
import { gridFilteredSortedRowEntriesSelector } from "@mui/x-data-grid";

const isNumericValue = (value: any) => {
  return value !== null && value !== undefined && value !== "" && !isNaN(Number(value));
};

const toNumberOrEmpty = (value: any) => {
  return isNumericValue(value) ? Number(value) : "";
};

/**
 * Ilyen objektumokat bont külön oszlopokra:
 *
 * {
 *   currency: { name: "HUF" },
 *   value: 40000,
 *   value_pp: 3077
 * }
 */
const isValueWithCurrencyAndPP = (value: any) => {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    value?.currency &&
    (
      Object.prototype.hasOwnProperty.call(value, "value") ||
      Object.prototype.hasOwnProperty.call(value, "value_pp")
    )
  );
};

const getSafeCurrencyName = (value: any) => {
  return value?.currency?.name || value?.currency?.code || "Currency";
};

const appendValueWithCurrencyColumns = (acc: any, col: any, cellValue: any) => {
  const currencyName = getSafeCurrencyName(cellValue);

  acc[`${col.headerName} (${currencyName})`] = toNumberOrEmpty(cellValue?.value);

  if (Object.prototype.hasOwnProperty.call(cellValue, "value_pp")) {
    acc[`${col.headerName} PP (${currencyName})`] = toNumberOrEmpty(
      cellValue?.value_pp
    );
  }

  return acc;
};

export const exportToXLSX = (
  rows: any,
  columns: any,
  apiRef: any,
  schema: any,
  searchParams: any
) => {
  /**
   * A DataGrid aktuálisan szűrt + rendezett sorait exportáljuk.
   * Ez fontos, mert a user azt várja, amit a táblában lát.
   */
  const filteredSortedRowsAndIds = gridFilteredSortedRowEntriesSelector(apiRef);
  const visibleRows = filteredSortedRowsAndIds.map((row: any) => row?.model);

  const worksheetData = visibleRows.map((row: any) => {
    return columns.reduce((acc: any, col: any) => {
      if (!col?.field || !col?.headerName) {
        return acc;
      }

      let cellValue = row[col.field];

      /**
       * Dinamikus ár objektum bontás.
       * Ez minden schema-ban működik, ahol ilyen adatstruktúra jön:
       *
       * {
       *   value: 40000,
       *   value_pp: 3077,
       *   currency: { name: "HUF" }
       * }
       */
      if (isValueWithCurrencyAndPP(cellValue)) {
        return appendValueWithCurrencyColumns(acc, col, cellValue);
      }

      /**
       * Régi costs típusú mezők kezelése:
       *
       * {
       *   EUR: 100,
       *   HUF: 40000
       * }
       *
       * Ezt is jobb külön numerikus oszlopokra bontani,
       * hogy Excelben lehessen számolni vele.
       */
      if (
        cellValue &&
        typeof cellValue === "object" &&
        !Array.isArray(cellValue) &&
        (Object.prototype.hasOwnProperty.call(cellValue, "EUR") ||
          Object.prototype.hasOwnProperty.call(cellValue, "HUF"))
      ) {
        if (Object.prototype.hasOwnProperty.call(cellValue, "EUR")) {
          acc[`${col.headerName} (EUR)`] = toNumberOrEmpty(cellValue?.EUR);
        }

        if (Object.prototype.hasOwnProperty.call(cellValue, "HUF")) {
          acc[`${col.headerName} (HUF)`] = toNumberOrEmpty(cellValue?.HUF);
        }

        return acc;
      }

      /**
       * Tömbök kezelése.
       * Pl. guide_reservations, guides, services, stb.
       */
      if (Array.isArray(cellValue)) {
        cellValue = cellValue
          .map((item: any) => {
            if (col.field === "guide_reservations") {
              if (item?.guide?.phone) {
                return `${item?.guide?.name} (${item?.guide?.phone})`;
              }

              return item?.guide?.name;
            }

            if (item?.name) {
              return item.name;
            }

            if (item?.title) {
              return item.title;
            }

            if (typeof item === "string" || typeof item === "number") {
              return item;
            }

            return "";
          })
          .filter(Boolean)
          .join(", ");

        acc[col.headerName] = cellValue || "";
        return acc;
      }

      /**
       * Objektumok általános kezelése.
       * Pl. status, group, activity, service_provider stb.
       */
      if (cellValue && typeof cellValue === "object") {
        if (cellValue?.name) {
          cellValue = cellValue.name;
        } else if (cellValue?.title) {
          cellValue = cellValue.title;
        } else if (cellValue?.label) {
          cellValue = cellValue.label;
        } else {
          cellValue = "";
        }
      }

      /**
       * Egyszerű cellaérték.
       */
      acc[col.headerName] =
        cellValue !== null && cellValue !== undefined ? cellValue : "";

      return acc;
    }, {});
  });

  const worksheet = XLSX.utils.json_to_sheet(worksheetData);

  /**
   * Opcionális: numerikus cellák típusának biztosítása.
   * A json_to_sheet általában felismeri a number típusokat,
   * de ez segít, ha valahol stringként csúszna be szám.
   */
  Object.keys(worksheet).forEach((cellKey) => {
    if (cellKey[0] === "!") {
      return;
    }

    const cell = worksheet[cellKey];

    if (typeof cell.v === "number") {
      cell.t = "n";
    }
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "DataGrid Export");

  let filename = schema?.pluralName || schema?.name || "export";

  if (searchParams && searchParams.get("from") && searchParams.get("to")) {
    filename += `_${searchParams.get("from")}-${searchParams.get("to")}`;
  }

  XLSX.writeFile(workbook, `${filename}.xlsx`);
};